import { useEffect, useState } from 'react';
import {
  errorMessage,
  financeDelete,
  financeGet,
  financeSend,
  isManager,
  num,
  useFinanceCodes,
  type FinanceCode,
  type FinanceCtx,
  type FinanceSettings,
} from '../financeApi';

type Tab = 'income' | 'expense' | 'department' | 'church';

const TAB_INFO: Record<Exclude<Tab, 'church'>, { label: string; desc: string }> = {
  income: { label: '헌금·수입 항목', desc: '헌금 입력·예산·결산에 쓰는 수입 항목입니다. "영수증 대상"을 끄면 기부금 영수증 합산에서 빠집니다(예: 기타수입).' },
  expense: { label: '지출 계정과목', desc: '지출 결의서·지출 내역·예산에 쓰는 계정과목입니다.' },
  department: { label: '부서', desc: '지출 청구와 부서별 예산에 쓰는 부서입니다. 부서를 고르지 않은 지출은 "공통"으로 집계됩니다.' },
};

function CodeList({ ctx, kind, codes, reload }: { ctx: FinanceCtx; kind: FinanceCode['kind']; codes: FinanceCode[]; reload: () => void }) {
  const editable = isManager(ctx);
  const list = codes.filter((c) => c.kind === kind).sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);
  const [newName, setNewName] = useState('');
  const [editing, setEditing] = useState<{ id: number; name: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = (promise: Promise<unknown>) => {
    setError(null);
    promise.then(reload).catch((err) => setError(errorMessage(err, '처리하지 못했습니다.')));
  };

  const add = () => {
    if (!newName.trim()) return;
    run(financeSend(ctx, 'post', '/codes', { kind, name: newName.trim() }).then(() => setNewName('')));
  };

  const move = (index: number, dir: -1 | 1) => {
    if (!list[index + dir]) return;
    // 두 항목을 바꾼 순서대로 0..n을 다시 매기고, 값이 달라진 항목만 저장
    const reordered = [...list];
    [reordered[index], reordered[index + dir]] = [reordered[index + dir], reordered[index]];
    run(Promise.all(
      reordered
        .map((code, order) => ({ code, order }))
        .filter(({ code, order }) => code.sortOrder !== order)
        .map(({ code, order }) => financeSend(ctx, 'put', `/codes/${code.id}`, { sortOrder: order })),
    ));
  };

  const rename = () => {
    if (!editing?.name.trim()) return;
    const code = list.find((c) => c.id === editing.id);
    if (code && code.name !== editing.name.trim()
      && !window.confirm(`'${code.name}'을(를) '${editing.name.trim()}'(으)로 바꾸면 기존 내역·예산의 이름도 함께 바뀝니다. 계속할까요?`)) return;
    run(financeSend(ctx, 'put', `/codes/${editing.id}`, { name: editing.name.trim() }).then(() => setEditing(null)));
  };

  const remove = (code: FinanceCode) => {
    if (!window.confirm(`'${code.name}'을(를) 삭제할까요? 이미 내역에 쓰인 항목은 삭제 대신 사용 중지됩니다.`)) return;
    run(financeDelete<{ deactivated: boolean }>(ctx, `/codes/${code.id}`).then((res) => {
      if (res.deactivated) window.alert('이미 사용된 항목이라 사용 중지로 바꿨습니다. 과거 내역에는 그대로 표시됩니다.');
    }));
  };

  return (
    <div className="manage-card-block">
      <div className="manage-table-wrap">
        <table className="manage-table">
          <thead>
            <tr>
              <th style={{ width: 70 }}>순서</th>
              <th>이름</th>
              {kind === 'income' && <th style={{ width: 110 }}>영수증 대상</th>}
              <th style={{ width: 90 }}>사용</th>
              {editable && <th style={{ width: 150 }}>관리</th>}
            </tr>
          </thead>
          <tbody>
            {list.map((code, i) => (
              <tr key={code.id} className={code.active ? '' : 'is-excluded'}>
                <td>
                  {editable && (
                    <div className="manage-move-btns manage-move-btns--row">
                      <button type="button" disabled={i === 0} onClick={() => move(i, -1)}>▲</button>
                      <button type="button" disabled={i === list.length - 1} onClick={() => move(i, 1)}>▼</button>
                    </div>
                  )}
                </td>
                <td>
                  {editing?.id === code.id ? (
                    <div className="fin-inline">
                      <input className="fin-cell-input" value={editing.name} autoFocus
                        onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                        onKeyDown={(e) => e.key === 'Enter' && rename()} />
                      <button type="button" className="manage-btn manage-btn--sm manage-btn--primary" onClick={rename}>저장</button>
                      <button type="button" className="manage-btn manage-btn--sm" onClick={() => setEditing(null)}>취소</button>
                    </div>
                  ) : (
                    <b>{code.name}</b>
                  )}
                </td>
                {kind === 'income' && (
                  <td>
                    <label className="fin-check">
                      <input type="checkbox" checked={Boolean(code.receiptable)} disabled={!editable}
                        onChange={(e) => run(financeSend(ctx, 'put', `/codes/${code.id}`, { receiptable: e.target.checked }))} />
                      {code.receiptable ? '대상' : '제외'}
                    </label>
                  </td>
                )}
                <td>
                  <label className="fin-check">
                    <input type="checkbox" checked={Boolean(code.active)} disabled={!editable}
                      onChange={(e) => run(financeSend(ctx, 'put', `/codes/${code.id}`, { active: e.target.checked }))} />
                    {code.active ? '사용' : '중지'}
                  </label>
                </td>
                {editable && (
                  <td>
                    <div className="manage-row-actions">
                      <button type="button" className="manage-btn manage-btn--sm" onClick={() => setEditing({ id: code.id, name: code.name })}>이름 변경</button>
                      <button type="button" className="manage-btn manage-btn--sm manage-btn--danger" onClick={() => remove(code)}>삭제</button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editable && (
        <div className="fin-add-row">
          <input value={newName} placeholder="새 이름" maxLength={50}
            onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} />
          <button type="button" className="manage-btn manage-btn--primary manage-btn--sm" onClick={add}>추가</button>
        </div>
      )}
      {error && <div className="fin-inline-error">{error}</div>}
    </div>
  );
}

function ChurchInfo({ ctx }: { ctx: FinanceCtx }) {
  const editable = isManager(ctx);
  const [form, setForm] = useState<FinanceSettings | null>(null);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    financeGet<FinanceSettings>(ctx, '/settings')
      .then(setForm)
      .catch((err) => setMessage({ ok: false, text: errorMessage(err, '설정을 불러오지 못했습니다.') }));
  }, [ctx]);

  if (!form) return message ? <div className="fin-inline-error">{message.text}</div> : <div className="manage-state">불러오는 중입니다...</div>;

  const field = (key: keyof FinanceSettings, label: string, placeholder = '', full = false) => (
    <label className={`manage-field${full ? ' manage-field--full' : ''}`}>
      <span>{label}</span>
      <input disabled={!editable} value={String(form[key] ?? '')} placeholder={placeholder}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
    </label>
  );

  const save = () => {
    setSaving(true);
    setMessage(null);
    financeSend(ctx, 'put', '/settings', { ...form })
      .then(() => setMessage({ ok: true, text: '저장했습니다.' }))
      .catch((err) => setMessage({ ok: false, text: errorMessage(err, '저장하지 못했습니다.') }))
      .finally(() => setSaving(false));
  };

  return (
    <div className="manage-card-block fin-block">
      <h3 className="fin-subtitle">기부금 영수증 발행 정보</h3>
      <div className="manage-form manage-form--grid">
        {field('churchName', '단체명(교회 이름)', '예: 하드림교회')}
        {field('registrationNo', '고유번호(사업자등록번호)', '000-00-00000')}
        {field('representative', '대표자', '담임목사 이름')}
        {field('phone', '전화번호')}
        {field('address', '소재지', '', true)}
      </div>
      <h3 className="fin-subtitle">이월 잔액</h3>
      <p className="fin-muted">시스템을 쓰기 시작한 날의 통장·현금 잔액을 넣으면 대시보드의 현재 잔액과 결산서의 전기 이월이 이 금액부터 계산됩니다.</p>
      <div className="manage-form manage-form--grid">
        <label className="manage-field">
          <span>기준일</span>
          <input type="date" disabled={!editable} value={form.openingDate ?? ''} onChange={(e) => setForm({ ...form, openingDate: e.target.value || null })} />
        </label>
        <label className="manage-field">
          <span>기준일 잔액</span>
          <input inputMode="numeric" disabled={!editable} value={form.openingBalance ? num(form.openingBalance) : ''} placeholder="0"
            onChange={(e) => setForm({ ...form, openingBalance: Number(e.target.value.replace(/[^\d-]/g, '')) || 0 })} />
        </label>
      </div>
      {message && <div className={message.ok ? 'fin-inline-ok' : 'fin-inline-error'}>{message.text}</div>}
      {editable ? (
        <div className="fin-actions">
          <button type="button" className="manage-btn manage-btn--primary" onClick={save} disabled={saving}>{saving ? '저장 중...' : '저장'}</button>
        </div>
      ) : (
        <p className="fin-muted fin-right">설정 변경은 재정부장만 할 수 있습니다.</p>
      )}
    </div>
  );
}

// 재정 설정: 헌금 항목·계정과목·부서 코드와 교회 정보(영수증)·이월 잔액. 변경은 재정부장만.
export default function FinanceSettingsPage({ ctx }: { ctx: FinanceCtx }) {
  const { codes, reload, loaded } = useFinanceCodes(ctx);
  const [tab, setTab] = useState<Tab>('income');

  return (
    <div className="manage-panel">
      <div className="manage-panel-header">
        <div>
          <h2 className="manage-panel-title">재정 설정</h2>
          <p className="manage-panel-desc">{tab === 'church' ? '기부금 영수증에 인쇄될 교회 정보와 잔액 계산의 기준이 되는 이월 잔액을 설정합니다.' : TAB_INFO[tab].desc}</p>
        </div>
      </div>
      <div className="manage-filterbar">
        <div className="manage-seg">
          {(['income', 'expense', 'department'] as const).map((k) => (
            <button key={k} type="button" className={`manage-seg-btn${tab === k ? ' is-active' : ''}`} onClick={() => setTab(k)}>{TAB_INFO[k].label}</button>
          ))}
          <button type="button" className={`manage-seg-btn${tab === 'church' ? ' is-active' : ''}`} onClick={() => setTab('church')}>교회 정보·이월 잔액</button>
        </div>
      </div>
      {tab === 'church' ? (
        <ChurchInfo ctx={ctx} />
      ) : loaded ? (
        <CodeList ctx={ctx} kind={tab} codes={codes} reload={reload} />
      ) : (
        <div className="manage-state">불러오는 중입니다...</div>
      )}
    </div>
  );
}
